// src/components/Kpis/PeriodFilter.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./PeriodFilter.css";

// Presets suportados
// HOJE | 7D | 30D | MES | CUSTOM

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toInputDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

function fromInputDate(v) {
  if (!v) return null;
  // v = YYYY-MM-DD
  const [y, m, d] = String(v).split("-").map((x) => Number(x));
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function startOfDay(d) {
  const dt = new Date(d);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function endOfDay(d) {
  const dt = new Date(d);
  dt.setHours(23, 59, 59, 999);
  return dt;
}

function getRangeByPreset(preset) {
  const hoje = new Date();
  const end = endOfDay(hoje);

  if (preset === "HOJE") {
    return { start: startOfDay(hoje), end };
  }

  if (preset === "7D") {
    const s = new Date(hoje);
    s.setDate(s.getDate() - 6);
    return { start: startOfDay(s), end };
  }

  if (preset === "30D") {
    const s = new Date(hoje);
    s.setDate(s.getDate() - 29);
    return { start: startOfDay(s), end };
  }

  if (preset === "MES") {
    const s = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    return { start: startOfDay(s), end };
  }

  return { start: null, end: null };
}

/**
 * PeriodFilter
 * - value: { preset, start, end }
 * - onApply: (nextValue) => void
 */
export default function PeriodFilter({ value, onApply }) {
  const initial = useMemo(() => {
    const v = value || {};
    const preset = v.preset || "30D";
    const range = getRangeByPreset(preset);
    return {
      preset,
      start: v.start ? new Date(v.start) : range.start,
      end: v.end ? new Date(v.end) : range.end,
    };
  }, [value]);

  const [preset, setPreset] = useState(initial.preset);
  const [start, setStart] = useState(initial.start);
  const [end, setEnd] = useState(initial.end);

  // se a página trocar o value (ex.: navegar), sincroniza
  useEffect(() => {
    setPreset(initial.preset);
    setStart(initial.start);
    setEnd(initial.end);
  }, [initial.preset, initial.start, initial.end]);

  const handlePresetChange = (p) => {
    setPreset(p);
    if (p !== "CUSTOM") {
      const r = getRangeByPreset(p);
      setStart(r.start);
      setEnd(r.end);
    }
  };

  const disabledCustom = preset !== "CUSTOM";

  const aplicar = () => {
    // Normaliza datas
    let s = start ? startOfDay(start) : null;
    let e = end ? endOfDay(end) : null;

    if (preset !== "CUSTOM") {
      const r = getRangeByPreset(preset);
      s = r.start;
      e = r.end;
    }

    // Se custom e faltou algo, não aplica
    if (preset === "CUSTOM" && (!s || !e)) return;

    onApply && onApply({ preset, start: s, end: e });
  };

  return (
    <div className="kpi-period-filter">
      <div className="kpi-period-row">
        <label className="kpi-period-label">
          <span>Período</span>
          <select
            className="kpi-period-select"
            value={preset}
            onChange={(e) => handlePresetChange(e.target.value)}
          >
            <option value="HOJE">Hoje</option>
            <option value="7D">Últimos 7 dias</option>
            <option value="30D">Últimos 30 dias</option>
            <option value="MES">Mês atual</option>
            <option value="CUSTOM">Personalizado</option>
          </select>
        </label>

        <label className="kpi-period-label">
          <span>De</span>
          <input
            className="kpi-period-input"
            type="date"
            disabled={disabledCustom}
            value={toInputDate(start)}
            onChange={(e) => setStart(fromInputDate(e.target.value))}
          />
        </label>

        <label className="kpi-period-label">
          <span>Até</span>
          <input
            className="kpi-period-input"
            type="date"
            disabled={disabledCustom}
            value={toInputDate(end)}
            onChange={(e) => setEnd(fromInputDate(e.target.value))}
          />
        </label>

        <button type="button" className="kpi-period-apply" onClick={aplicar}>
          Aplicar
        </button>
      </div>
      {preset === "CUSTOM" && (!start || !end) && (
        <div className="kpi-period-hint">Selecione a data inicial e final.</div>
      )}
    </div>
  );
}
